const SAFE_RENDERED_ARTIFACT_PROTOCOLS = new Set(['http:', 'https:', 'mailto:'])
const SAFE_ARTIFACT_URL_PROTOCOLS = new Set(['http:', 'https:'])
const ARTIFACT_URL_PARSE_BASE = 'https://github.com'
const DEFAULT_ARTIFACT_LINK_NAME = 'Link'
const SLACK_ARTIFACT_LINK_NAME = 'Slack conversation'
const VALID_ARTIFACT_HOSTNAME = /^[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/i

/**
 * Returns an artifact href when it uses a protocol safe for rendering.
 * Relative and anchor-only hrefs are resolved against the current page by the browser.
 */
export function getSafeArtifactHref(href: string | undefined | null): string | undefined {
  if (!href) return undefined

  try {
    const {protocol} = new URL(href, ARTIFACT_URL_PARSE_BASE)
    return SAFE_RENDERED_ARTIFACT_PROTOCOLS.has(protocol) ? href : undefined
  } catch {
    return undefined
  }
}

/** Returns whether a form value is an absolute HTTP or HTTPS artifact URL. */
export function isValidArtifactUrl(value: string): boolean {
  try {
    // A base would allow relative values, and browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const {protocol} = new URL(value)
    return SAFE_ARTIFACT_URL_PROTOCOLS.has(protocol)
  } catch {
    return false
  }
}

/** Returns a valid artifact name for a link without a user-provided title. */
export function getDefaultArtifactLinkName(url: string): string {
  try {
    // Browser globals are unavailable during SSR.
    // eslint-disable-next-line no-restricted-syntax
    const hostname = new URL(url).hostname.replace(/\.+$/, '')

    if (hostname === 'slack.com' || hostname.endsWith('.slack.com')) {
      return SLACK_ARTIFACT_LINK_NAME
    }

    return VALID_ARTIFACT_HOSTNAME.test(hostname) ? hostname : DEFAULT_ARTIFACT_LINK_NAME
  } catch {
    return DEFAULT_ARTIFACT_LINK_NAME
  }
}

/** Returns the concise title used when an artifact name is empty or repeats its URL. */
export function getArtifactLinkDisplayName(name: string, url: string): string {
  let cleanedUrl = url.trim().replace(/^https?:\/\//i, '')
  while (cleanedUrl.endsWith('/')) cleanedUrl = cleanedUrl.slice(0, -1)
  const trimmedName = name.trim()

  return !trimmedName || trimmedName === url.trim() || trimmedName === cleanedUrl ? cleanedUrl || name : name
}
